// Market expansion M6 — pure types + safe shaping for the
// get_market_coverage_overview() payload. No I/O. Soft, total
// functions: a malformed/empty RPC payload yields the honest-empty
// view (modelled 0, nothing declared modelled, nothing declined) —
// never fabricated.

export interface MarketFamilyCount {
  readonly family: string;
  readonly n: number;
}
export interface MarketTierCount {
  readonly tier: string;
  readonly n: number;
}
export interface DeclinedMarket {
  readonly canonical_code: string;
  readonly label: string;
  readonly reason: string;
}

export interface MarketCoverageOverview {
  readonly generated_at: string;
  readonly modelled_total: number;
  readonly by_family: readonly MarketFamilyCount[];
  readonly by_tier: readonly MarketTierCount[];
  readonly declined: readonly DeclinedMarket[];
}

export const EMPTY_MARKET_COVERAGE: MarketCoverageOverview = {
  generated_at: "",
  modelled_total: 0,
  by_family: [],
  by_tier: [],
  declined: [],
};

function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : 0;
}
function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Total, defensive shaping of the raw RPC jsonb. Never throws. */
export function shapeMarketCoverage(raw: unknown): MarketCoverageOverview {
  if (!raw || typeof raw !== "object") return EMPTY_MARKET_COVERAGE;
  const o = raw as Record<string, unknown>;

  const by_family: MarketFamilyCount[] = Array.isArray(o.by_family)
    ? o.by_family
        .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
        .map((r) => ({ family: str(r.family), n: num(r.n) }))
        .filter((r) => r.family.length > 0)
    : [];

  const by_tier: MarketTierCount[] = Array.isArray(o.by_tier)
    ? o.by_tier
        .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
        .map((r) => ({ tier: str(r.tier), n: num(r.n) }))
        .filter((r) => r.tier.length > 0)
    : [];

  const declined: DeclinedMarket[] = Array.isArray(o.declined)
    ? o.declined
        .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
        .map((r) => ({
          canonical_code: str(r.canonical_code),
          label: str(r.label),
          reason: str(r.reason),
        }))
        .filter((r) => r.canonical_code.length > 0)
    : [];

  return {
    generated_at: str(o.generated_at),
    modelled_total: num(o.modelled_total),
    by_family,
    by_tier,
    declined,
  };
}
